import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthResponse } from '../../classes/auth-response';
import { User } from '../../classes/user.class';
import { LocalStorage } from '../../storage.class';
import { ApiResponse, ApiService } from '../api/api.service';
import { ConfirmDialogService } from '../confirm-dialog-service/confirm-dialog.service';
import { AuthorisationService } from './authorisation.service';
import { Functionalities } from './functionalities';

@Injectable()
export class AuthService {
    loggedIn = new EventEmitter<boolean>();

    constructor(
        public apiService: ApiService,
        private router: Router,
        private confirmDialogService: ConfirmDialogService
    ) {}

    switchToParent() {
        const parentToken = LocalStorage.getParentUserToken();
        LocalStorage.logoutUser();
        this.refresh(parentToken).subscribe({
            next: () => {
                window.location.href = '/';
            },
            error: () => {
                LocalStorage.unsetParentUser();
                this.router.navigate(['/logout'], { replaceUrl: true });
            },
        });
    }

    switchToProfile(token: string) {
        const parentToken = LocalStorage.getUserToken();
        const parentUser = LocalStorage.getUser();
        LocalStorage.logoutUser();
        LocalStorage.setParentUser(parentToken, parentUser);
        return this.refresh(token);
    }

    loginAsUser(userId: number) {
        return this.apiService.postCall$<string>('auth/loginAsUser', { user_id: userId });
    }

    loginAsDirkUser(userId: number) {
        return this.apiService.postCall$<{
            access_token: string;
            parent_access_token: string;
            parent_user: { id: number; name: string };
        }>('auth/loginAsDirkUser', { user_id: userId });
    }

    loginPin(email: string, pin: string) {
        return this.apiService
            .postCall$<AuthResponse>('auth/login-code', { pin, email })
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    login(email: string, password: string) {
        return this.apiService
            .postCall$<AuthResponse>('auth/login', { email, password })
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    loginEmail(email: string) {
        return this.apiService.postCall$<boolean>('auth/login-email', { email });
    }

    loginType(email: string) {
        return this.apiService.postCall$<{
            loginType: string;
        }>('auth/login-type', { email });
    }

    handleMicrosoftCallback(code: string, state: string) {
        return this.apiService
            .getCall$<AuthResponse>('auth/microsoft/callback', {
                code,
                state,
                redirect_origin: window.location.origin,
            })
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    loginWithMicrosoft() {
        return this.apiService.getCallData$<{ auth_url: string }>('auth/microsoft/login', {
            redirect_origin: window.location.origin,
        });
    }

    redirectAfterLogin() {
        if (
            !AuthorisationService.hasFunctionality(Functionalities.PlanningOverview) &&
            !AuthorisationService.hasFunctionality(Functionalities.OwnHours) &&
            environment.dirkAppUrl
        ) {
            const accessToken = LocalStorage.getUserToken();
            LocalStorage.logoutUser(); // Wis planning sessie want je hebt geen rechten op planning
            window.location.href = `${environment.dirkAppUrl}take-over?token=${encodeURIComponent(accessToken)}`;
            return;
        }

        if (AuthorisationService.hasRole(environment.roles.FormerEmployee)) {
            this.router.navigateByUrl('/periodeverrekening');
            return;
        }

        if (
            !AuthorisationService.hasAnyFunctionality([
                Functionalities.PlanningOverviewOwn,
                Functionalities.PlanningOverviewManagement,
                Functionalities.HourCheck,
            ]) &&
            AuthorisationService.hasFunctionality(Functionalities.Plannable) &&
            AuthorisationService.hasFunctionality(Functionalities.OwnHours) &&
            window.matchMedia('(max-width: 991px)').matches
        ) {
            this.router.navigateByUrl('/weekschedule');
            return;
        }

        this.router.navigateByUrl(
            AuthorisationService.hasFunctionality(Functionalities.PlanningOverview) ? '/' : '/weekschedule'
        );
    }

    public logout() {
        if (LocalStorage.getParentUserToken()) {
            this.switchToParent();
        } else {
            this.router.navigateByUrl('/login');
            this.confirmDialogService
                .confirm({
                    title: 'Uitloggen',
                    text: `U wordt uitgelogd... <i class="fas fa-spinner fa-spin"></i>`,
                    confirm: null,
                    cancel: null,
                })
                .then(() => {
                    console.log('LOGOUT A');
                    window.location.reload();
                });
            this.apiService.postCall('auth/logout').then(
                () => {
                    LocalStorage.logoutUser();
                    setTimeout(() => {
                        console.log('LOGOUT B');
                        window.location.reload();
                    });
                },
                () => {
                    LocalStorage.logoutUser();
                    setTimeout(() => {
                        console.log('LOGOUT C');
                        window.location.reload();
                    });
                }
            );
        }
    }

    public getUserDetails() {
        return this.apiService.getCallData$<User>('auth/me').pipe(
            tap(response => LocalStorage.setUser(response)),
            shareReplay(1)
        );
    }

    public requestRestorePassword(email: string): Observable<ApiResponse<void>> {
        return this.apiService.postCall$<void>('auth/request-restore', { email });
    }

    public verifyResetToken(token: string): Observable<{ valid: boolean }> {
        return this.apiService.getCallData$<{ valid: boolean }>('auth/verify-reset-token', { token });
    }

    public restorePassword(token: string, password: string) {
        return this.apiService
            .postCall$<AuthResponse>('auth/restore', { password, token })
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    public refresh(token?: string) {
        if (token) {
            return this.apiService
                .postCall$<AuthResponse>('auth/refresh', { token })
                .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
        }
        return this.apiService
            .getCall$<AuthResponse>('auth/refresh')
            .pipe(map(response => this.handleResponse(response.data)));
    }

    private handleResponse(authResponse: AuthResponse) {
        if (!!authResponse.access_token) {
            LocalStorage.setUserToken(authResponse.access_token);
            LocalStorage.setUser(authResponse.user);
            LocalStorage.setExpireTimeToken(authResponse.expires_in);
            this.loggedIn.emit(true);
            return authResponse;
        } else {
            this.loggedIn.emit(false);
            this.router.navigate(['logout']);
        }
    }
}
